// WHY (#1057): CONTAINER requests carry one general carrier group
// (request-wide scope) instead of per-destination sections. Mirrors the
// surface of `CarriersDestinationSection` but with a neutral "General
// rates" header and `arrivalCityId: null` on the add path so the wire
// lands a request-wide carrier row.
//
// @aiPurpose Render the single CONTAINER-wide carrier group (#1057).
// @aiInvariant All bid rows on a CONTAINER request carry `arrivalCityId
//   === null` (the legacy STANDARD per-destination scope does not
//   apply); the picker filters duplicates against the same flat list so
//   a carrier added twice surfaces the inline "already added" UI error.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorCode, hasPermission, RequestCarrierState } from '@magimp/shared';
import { Button, Icon, Select } from '../../../../ui/index.js';
import { useAuthStore } from '../../../../stores/auth.js';
import {
  useAddCarrier,
  useCarriersCatalog,
  useDeleteCarrier,
  useDeleteCarrierBid,
  usePatchCarrier,
  usePickWinner,
} from '../../queries.js';
import {
  canAddFirstCarrier,
  canManageRequestCarriers,
  canPickWinner,
  canReviveCarriersOnCannotTransport,
} from '../../role.js';
import { ApiClientError, errorMessage } from '../../../../api/errors.js';
import type { CarrierBid, CarrierBidQuote, RequestView } from '../../types.js';
import { CarrierBidCard } from './CarrierBidCard.js';

export interface CarriersGeneralSectionProps {
  request: RequestView;
  cheapestBidId: string | null;
  fxRates: Record<string, number>;
  onAddBid: (bid: CarrierBid) => void;
  onEditBid: (bid: CarrierBid, quote: CarrierBidQuote) => void;
  onReject: (bid: CarrierBid) => void;
  onError: (message: string) => void;
}

export function CarriersGeneralSection({
  request: r,
  cheapestBidId,
  fxRates,
  onAddBid,
  onEditBid,
  onReject,
  onError,
}: CarriersGeneralSectionProps) {
  const { t } = useTranslation(['requests', 'common']);
  const user = useAuthStore((s) => s.user);
  const permissions = useAuthStore((s) => s.permissions);
  const addCarrierMutation = useAddCarrier(r.id);
  const patchCarrierMutation = usePatchCarrier(r.id);
  const deleteCarrierMutation = useDeleteCarrier(r.id);
  const deleteBidMutation = useDeleteCarrierBid(r.id);
  const pickWinnerMutation = usePickWinner(r.id);
  const [selectedCarrierId, setSelectedCarrierId] = useState('');
  const [duplicateError, setDuplicateError] = useState<string | null>(null);

  const party = {
    actorUserId: user?.id ?? null,
    managerId: r.managerId,
    logistId: r.logistId,
  };
  const canManage = canManageRequestCarriers(permissions, r.status, party);
  const canAddFirst = canAddFirstCarrier(permissions, r.status, r.carriers.length, party);
  const canRevive = canReviveCarriersOnCannotTransport(permissions, r.status, party);
  const showPicker = canManage || canAddFirst || canRevive;
  const canReadCarrierCatalog = hasPermission(permissions, 'carriers.read');
  const catalogQuery = useCarriersCatalog({ enabled: showPicker && canReadCarrierCatalog });
  const showCTA = canPickWinner(permissions, r.status, r.winnerBidId != null, party);

  // WHY (#1057): every CONTAINER row carries `arrivalCityId === null`;
  //   `r.carriers` is therefore the request-wide flat list.
  const carriers = r.carriers;
  const existingIds = useMemo(() => new Set(carriers.map((c) => c.carrier.id)), [carriers]);
  const addOptions = (catalogQuery.data?.items ?? [])
    .filter((c) => c.active && !existingIds.has(c.id))
    .map((c) => ({ value: c.id, label: c.name }));

  async function onAddCarrier(): Promise<void> {
    if (!selectedCarrierId) return;
    // WHY (#1057): server enforces "no duplicate carrier per request"
    //   (P2002 → 409). Surface the duplicate as a localized inline
    //   error so a manager who taps the same row twice gets immediate
    //   feedback without waiting for the round trip.
    if (existingIds.has(selectedCarrierId)) {
      setDuplicateError(t('requests:carriers.errors.carrierAlreadyInRequest'));
      return;
    }
    setDuplicateError(null);
    try {
      await addCarrierMutation.mutateAsync({
        carrierId: selectedCarrierId,
        arrivalCityId: null,
      });
      setSelectedCarrierId('');
    } catch (err) {
      // WHY (#1057): client-side `existingIds` covers the in-tab case
      //   but a sibling tab (or any concurrent operator) can land the
      //   same carrier between our catalog query and the POST. The
      //   server still rejects with `DuplicateRequestCarrierError`
      //   (DOMAIN_CONFLICT + carrierId on meta); surface the same
      //   localized inline error instead of letting the generic
      //   "Data conflict. Refresh the page" toast eat the context.
      if (isDuplicateCarrierConflict(err)) {
        setDuplicateError(t('requests:carriers.errors.carrierAlreadyInRequest'));
        return;
      }
      onError(errorMessage(err));
    }
  }

  async function onReset(bid: CarrierBid): Promise<void> {
    try {
      await patchCarrierMutation.mutateAsync({
        requestCarrierId: bid.id,
        body: { state: RequestCarrierState.PENDING },
      });
    } catch (err) {
      onError(errorMessage(err));
    }
  }

  async function onDelete(bid: CarrierBid): Promise<void> {
    try {
      await deleteCarrierMutation.mutateAsync(bid.id);
    } catch (err) {
      onError(errorMessage(err));
    }
  }

  async function onDeleteBid(bid: CarrierBid, quote: CarrierBidQuote): Promise<void> {
    try {
      await deleteBidMutation.mutateAsync({ requestCarrierId: bid.id, bidId: quote.id });
    } catch (err) {
      onError(errorMessage(err));
    }
  }

  return (
    <section
      aria-label={t('requests:carriers.generalSection.title')}
      data-arrival-city-id="general"
      data-testid="carriers-general-section"
      style={{ display: 'grid', gap: 10 }}
    >
      <header style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--fg-1)' }}>
          {t('requests:carriers.generalSection.title')}
        </h3>
        <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>
          {t('carriers.completedCount', {
            completed: carriers.filter((c) => c.state === 'answered' || c.state === 'rejected')
              .length,
            total: carriers.length,
          })}
        </span>
      </header>

      {showPicker && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <div style={{ flex: '1 1 220px', minWidth: 0 }}>
            <Select
              value={selectedCarrierId}
              placeholder={t('requests:carriers.addPlaceholder')}
              options={addOptions}
              onChange={(v) => {
                setSelectedCarrierId(v);
                if (duplicateError) setDuplicateError(null);
              }}
            />
          </div>
          <Button
            kind="primary"
            icon="plus"
            disabled={!selectedCarrierId || addCarrierMutation.isPending}
            onClick={() => {
              void onAddCarrier();
            }}
          >
            {t('requests:carriers.add')}
          </Button>
          {duplicateError && (
            <span
              role="alert"
              data-testid="carriers-general-duplicate-error"
              style={{
                fontSize: 12,
                color: 'var(--status-error-fg)',
                flexBasis: '100%',
                overflowWrap: 'anywhere',
              }}
            >
              {duplicateError}
            </span>
          )}
          {catalogQuery.isError && (
            <span
              style={{
                fontSize: 12,
                color: 'var(--status-error-fg)',
                flexBasis: '100%',
                overflowWrap: 'anywhere',
              }}
            >
              {errorMessage(catalogQuery.error)}
            </span>
          )}
        </div>
      )}

      {carriers.length === 0 ? (
        <div
          style={{
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8,
            color: 'var(--fg-3)',
            border: '1px dashed var(--border-2)',
            borderRadius: 6,
          }}
        >
          <Icon name="ship" size={20} style={{ color: 'var(--fg-4)' }} />
          <div style={{ fontSize: 12 }}>{t('carriers.empty')}</div>
        </div>
      ) : (
        <ul
          aria-label={t('requests:carriers.columns.carrier')}
          style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 10 }}
        >
          {carriers.map((c) => (
            <li key={c.id}>
              <CarrierBidCard
                bid={c}
                requestId={r.id}
                request={r}
                winnerBidId={r.winnerBidId}
                cheapestBidId={cheapestBidId}
                canManage={canManage}
                canRevive={canRevive}
                canPick={showCTA && c.state === 'answered'}
                fxRates={fxRates}
                onAddBid={() => onAddBid(c)}
                onEditBid={(quote) => onEditBid(c, quote)}
                onDeleteBid={(quote) => {
                  void onDeleteBid(c, quote);
                }}
                onReject={() => onReject(c)}
                onReset={() => {
                  void onReset(c);
                }}
                onDelete={() => {
                  void onDelete(c);
                }}
                onPickBid={(quote) => pickWinnerMutation.mutate(quote.id)}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// INVARIANT (#1057): the server signals a duplicate-on-request via
//   `ApiClientError { code: DOMAIN_CONFLICT, meta: { carrierId, ... } }`.
//   Match on `code` + `meta.carrierId` so a future addition of new
//   DOMAIN_CONFLICT shapes on the same endpoint cannot accidentally
//   trip this branch.
function isDuplicateCarrierConflict(err: unknown): boolean {
  if (!(err instanceof ApiClientError)) return false;
  if (err.code !== ErrorCode.DOMAIN_CONFLICT) return false;
  const meta = err.meta as { carrierId?: unknown } | undefined;
  return typeof meta?.carrierId === 'string';
}
